import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO, jsonLdBreadcrumb } from "@/lib/seo";
import { ArrowLeft, Lock, ShieldCheck } from "lucide-react";
import RequestAccessDialog from "@/components/app/RequestAccessDialog";

export default function PortfolioDetails() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [notes, setNotes] = useState(null);
  const [notesError, setNotesError] = useState(null);
  const [openRequest, setOpenRequest] = useState(false);
  const [myReq, setMyReq] = useState(null);

  useEffect(() => {
    api.get(`/marketplace/portfolios/${id}`).then((r) => setPortfolio(r.data)).catch(() => navigate("/portfolios"));
  }, [id, navigate]);

  useEffect(() => {
    if (!user) return;
    // Try to load protected notes — 403 is expected until approved.
    api.get(`/marketplace/portfolios/${id}/notes`)
      .then((r) => setNotes(r.data.notes || []))
      .catch((e) => setNotesError(e?.response?.data?.detail || "Access not yet approved"));
    // Check whether the current buyer has an existing request
    api.get(`/marketplace/access-requests/mine`)
      .then((r) => {
        const mine = (r.data.items || []).find((x) => x.portfolio_id === id);
        if (mine) setMyReq(mine);
      })
      .catch(() => {});
  }, [id, user]);

  useSEO(portfolio ? {
    title: `${portfolio.title} — ${portfolio.summary?.note_count || 0}-Note Portfolio, ${fmtPct(portfolio.summary?.average_yield || 0)} Avg Yield | FinanceLocal`,
    description: `${portfolio.title} vehicle note portfolio. ${portfolio.summary?.note_count} notes · ${fmtMoney(portfolio.summary?.total_upb || 0)} UPB · ${fmtPct(portfolio.summary?.average_yield || 0)} average yield · ${fmtMoney(portfolio.asking_price || 0)} asking price.`,
    canonical: `https://myfinancelocal.com/portfolios/${id}`,
    jsonLd: [
      jsonLdBreadcrumb([
        { name: "Home", url: "https://myfinancelocal.com/" },
        { name: "Portfolios", url: "https://myfinancelocal.com/portfolios" },
        { name: portfolio.title, url: `https://myfinancelocal.com/portfolios/${id}` },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "FinancialProduct",
        "name": portfolio.title,
        "description": (portfolio.description || "").slice(0, 400),
        "category": "Vehicle-backed note portfolio",
        "provider": { "@type": "Organization", "name": "FinanceLocal", "url": "https://myfinancelocal.com/" },
        "amount": { "@type": "MonetaryAmount", "currency": "USD", "value": portfolio.asking_price },
        "annualPercentageRate": portfolio.summary?.average_yield,
      },
    ],
  } : { title: "Portfolio — FinanceLocal", canonical: `https://myfinancelocal.com/portfolios/${id}` });

  if (!portfolio) return <div className="mx-auto max-w-7xl px-4 py-16 text-muted-foreground" data-testid="portfolio-loading">Loading…</div>;

  const s = portfolio.summary || {};
  const fees = portfolio.marketplace_fees || {};
  const isOwner = user && user.user_id === portfolio.seller_id;
  const isAdmin = user && user.role === "admin";
  const canSeeNotes = !!notes && notes.length > 0;

  return (
    <div data-testid="page-portfolio-details" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/portfolios" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="back-to-portfolios">
        <ArrowLeft className="w-4 h-4" /> Back to Portfolios
      </Link>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-10">
        {/* LEFT — Public summary */}
        <div>
          <div className="font-overline text-accent">Portfolio Listing</div>
          <h1 className="font-display text-4xl mt-2" data-testid="portfolio-title">{portfolio.title}</h1>
          {portfolio.description && <p className="text-muted-foreground mt-3 leading-relaxed">{portfolio.description}</p>}

          <section className="mt-10 border-y border-border py-8" data-testid="portfolio-summary">
            <div className="font-overline text-muted-foreground mb-4">Aggregate Summary</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-8">
              <Stat label="Notes" value={s.note_count} testId="summary-note-count" />
              <Stat label="Total UPB" value={fmtMoney(s.total_upb || 0)} testId="summary-upb" />
              <Stat label="Average Yield" value={fmtPct(s.average_yield || 0)} highlight testId="summary-yield" />
              <Stat label="Avg Seasoning" value={`${s.average_seasoning_weeks || 0} wk`} testId="summary-seasoning" />
              <Stat label="Avg Remaining" value={`${s.average_remaining_term_weeks || 0} wk`} testId="summary-remaining" />
              <Stat label="Asking Price" value={fmtMoney(portfolio.asking_price || 0)} highlight testId="summary-asking" />
            </div>
          </section>

          <DistributionTable title="Geographic Distribution" data={s.state_distribution_pct} testId="dist-state" />
          <DistributionTable title="Grade Distribution" data={s.grade_distribution_pct} testId="dist-grade" prefix="Grade " />
          <DistributionTable title="Loan Type / Category" data={s.category_distribution_pct} testId="dist-category" labelize />

          {/* Protected section — only visible after access approved */}
          <section className="mt-10 border-t border-border pt-8" data-testid="portfolio-protected">
            <div className="flex items-center gap-2 font-overline text-muted-foreground mb-4">
              {canSeeNotes || isOwner || isAdmin ? <ShieldCheck className="w-4 h-4 text-primary" /> : <Lock className="w-4 h-4" />}
              Individual Notes
            </div>
            {canSeeNotes ? (
              <div className="border border-border divide-y divide-border" data-testid="portfolio-notes-list">
                {notes.map((n) => (
                  <Link key={n.note_id} to={`/notes/${n.note_id}`} className="flex items-center justify-between px-4 py-3 hover:bg-muted/40"
                        data-testid={`portfolio-note-${n.note_id}`}
                        onClick={() => {
                          if (myReq?.status === "approved") {
                            api.post(`/marketplace/access-requests/${myReq.request_id}/log-view`, { doc_type: "note", doc_ref: n.note_id }).catch(() => {});
                          }
                        }}>
                    <div>
                      <div className="font-display text-lg">{n.vehicle_year} {n.vehicle_make} {n.vehicle_model}</div>
                      <div className="text-xs text-muted-foreground">Grade {n.grade} · {n.state}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-data text-primary">{fmtPct(n.estimated_yield)}</div>
                      <div className="text-xs text-muted-foreground font-mono-data">{fmtMoney(n.remaining_balance)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="border border-border p-6 bg-muted/20 text-sm text-muted-foreground" data-testid="portfolio-notes-locked">
                {user ? (myReq
                  ? `Your access request is ${myReq.status}. Individual note detail unlocks once the seller approves.`
                  : "Individual loan details, payment histories, contracts, titles, and collateral documents are protected. Submit an access request below to be reviewed by the seller.")
                : (
                  <>Please <Link to="/login" className="underline">sign in</Link> and submit an access request to see individual notes and supporting documents.</>
                )}
              </div>
            )}
          </section>
        </div>

        {/* RIGHT — Access request CTA + fee card */}
        <div className="space-y-6">
          <div className="border border-border p-6 space-y-4" data-testid="portfolio-access-card">
            <div className="font-overline text-muted-foreground">Access</div>
            {!user && (
              <Button data-testid="portfolio-signin-cta" onClick={() => navigate("/login")}
                className="w-full rounded-none uppercase tracking-wider h-11">Sign in to Request Access</Button>
            )}
            {user && isOwner && (
              <div className="text-sm text-muted-foreground">You own this portfolio. Manage access requests from your dashboard.</div>
            )}
            {user && !isOwner && !myReq && (
              <Button data-testid="portfolio-request-access-btn" onClick={() => setOpenRequest(true)}
                className="w-full rounded-none uppercase tracking-wider h-11">Request Portfolio Access</Button>
            )}
            {user && !isOwner && myReq && (
              <div className="text-sm space-y-1">
                <div className="font-overline text-muted-foreground text-xs">Your Request</div>
                <div className="font-mono-data" data-testid="portfolio-my-request-status">Status: {myReq.status}</div>
                {myReq.status === "info_requested" && (
                  <div className="border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs mt-2">
                    Seller requested more info: <b>{myReq.info_requested_message}</b>
                  </div>
                )}
                {myReq.status === "declined" && myReq.decline_reason && (
                  <div className="text-xs text-muted-foreground mt-2">Reason: {myReq.decline_reason}</div>
                )}
              </div>
            )}
          </div>

          <div className="border border-border p-6 space-y-3" data-testid="portfolio-fees-card">
            <div className="font-overline text-muted-foreground">Marketplace Fees</div>
            <div className="flex justify-between text-sm"><span>Buyer fee (2%)</span><span className="font-mono-data">{fmtMoney(fees.buyer_fee || 0)}</span></div>
            <div className="flex justify-between text-sm"><span>Seller fee (2%)</span><span className="font-mono-data">{fmtMoney(fees.seller_fee || 0)}</span></div>
            <div className="flex justify-between text-sm border-t border-border pt-2"><span>Combined</span><span className="font-mono-data font-semibold">{fmtMoney(fees.combined_fee || 0)}</span></div>
            {fees.minimum_applied && (
              <div className="text-[11px] text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-2" data-testid="portfolio-min-fee-notice">
                $500 minimum combined fee applied (calculated 4% would be below the platform floor).
              </div>
            )}
          </div>
        </div>
      </div>

      <RequestAccessDialog
        portfolio={portfolio}
        open={openRequest}
        onOpenChange={setOpenRequest}
        onSubmitted={(req) => { setMyReq(req); toast.success("Access request submitted"); }}
      />
    </div>
  );
}

function Stat({ label, value, highlight, testId }) {
  return (
    <div data-testid={testId}>
      <div className="font-overline text-muted-foreground text-xs">{label}</div>
      <div className={`font-mono-data text-2xl mt-1 ${highlight ? "text-primary" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

const CATEGORY_LABELS = {
  performing: "Performing", semi_performing: "Semi-Performing", non_performing: "Non-Performing",
  repo_ready: "Repo Ready", fresh_origination: "Fresh Origination",
};

function DistributionTable({ title, data, testId, prefix = "", labelize = false }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return (
    <section className="mt-8" data-testid={testId}>
      <div className="font-overline text-muted-foreground mb-3">{title}</div>
      <div className="border border-border divide-y divide-border">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between px-4 py-2 text-sm">
            <span>{labelize ? (CATEGORY_LABELS[k] || k) : `${prefix}${k}`}</span>
            <span className="font-mono-data">{v}%</span>
          </div>
        ))}
      </div>
    </section>
  );
}
